/**
 * Immutable documentation release selection.
 *
 * Language, version, and product form one routing/API identity. Keeping them
 * in one value prevents components from observing or sending mixed releases.
 */
export class Release {
    constructor({ language = null, version = null, product = null } = {}) {
        this.language = language;
        this.version = version;
        this.product = product;
        Object.freeze(this);
    }

    static from(value) {
        if (value instanceof Release) return value;
        return new Release(value);
    }

    static fromUrl(search = window.location.search) {
        const params = new URLSearchParams(search);
        return new Release({
            language: params.get('lang'),
            version: params.get('ver'),
            product: params.get('product'),
        });
    }

    get isComplete() {
        return Boolean(this.language && this.version && this.product);
    }

    with(changes = {}) {
        const has = (attribute) => Object.prototype.hasOwnProperty.call(changes, attribute);
        return new Release({
            language: has('language') ? changes.language : this.language,
            version: has('version') ? changes.version : this.version,
            product: has('product') ? changes.product : this.product,
        });
    }

    equals(other) {
        const release = Release.from(other);
        return this.language === release.language &&
            this.version === release.version &&
            this.product === release.product;
    }

    toSearchParams(initialParams) {
        const params = new URLSearchParams(initialParams);
        if (this.language) params.set('lang', this.language);
        if (this.version) params.set('ver', this.version);
        if (this.product) params.set('product', this.product);
        return params;
    }

    toKey(...parts) {
        return [this.language, this.version, this.product, ...parts].join(':');
    }
}

export const EMPTY_RELEASE = new Release();
